/**
 * V66 — 조직 휴가 캘린더 페이지
 *
 * 조직 관리 > 조직 휴가 캘린더
 *
 * 정책:
 *   - 데이터 SSoT: BE callable getOrgLeaveCalendar (FE Firestore 직접 읽기 금지)
 *   - 조직 범위: BE handleGetOrgManagementScope (FE 임의 계산 금지)
 *   - 표시 조건: leaveRequests.status==='approved' && approvalRequests.status==='approved'
 *
 * 표시 규칙:
 *   - 다일 휴가: 기간 내 모든 날짜 셀에 expand
 *   - 시간단위: '홍길동 연차 (13:00~17:00)'
 *   - 셀당 최대 3건 + +N명 칩
 *   - hover/click → 전체 popover
 */

import React, { useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import {
  useOrgLeaveCalendar,
  OrgLeaveCalendarItem,
} from '../../shared/hooks/useOrgLeaveCalendar';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useCalendarHolidays, CompanyCode } from '../../shared/hooks/useHolidays';
import { isKrRemoteFriday } from '../../shared/utils/krRemoteFriday';
import { formatCompOffDaysHours } from '../../shared/utils/formatCompOff';

const KOREAN_DAY = ['일', '월', '화', '수', '목', '금', '토'];
const MAX_VISIBLE_PER_CELL = 3;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function ymd(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * V66-FIX2: 휴가 라벨 결정
 *
 * 정책:
 *   - annual + morningHalf/afternoonHalf → 오전반차 / 오후반차
 *   - compensatory: totalDays 기반으로 시간/일 환산 (formatCompOffDaysHours 재사용, 1일=8시간)
 *     · 시간단위(hourly): "대체휴무 N시간 (HH:mm~HH:mm)"
 *     · 그 외: "대체휴무 N일" 또는 "대체휴무 N일 N시간" 또는 "대체휴무 N시간"
 *   - 일반 hourly (annual/etc): leaveTypeName + 시간 표시
 *   - 그 외: leaveTypeName 그대로 (퍼스널힐링/공가 등)
 */
function entryLabel(item: OrgLeaveCalendarItem): string {
  // V66: 휴직(LoA) 우선 분기 — 인사상태 우선 정책
  if (item.kind === 'leaveOfAbsence' || item.leaveTypeCode === 'leaveOfAbsence') {
    const name = item.loaTypeName || item.leaveTypeName || '휴직';
    return `휴직중 (${name})`;
  }

  // annual + 반차 단위
  if (item.leaveTypeCode === 'annual' && item.unit === 'morningHalf') return '오전반차';
  if (item.leaveTypeCode === 'annual' && item.unit === 'afternoonHalf') return '오후반차';

  // V66-FIX3: 시간단위 검출은 unit 만 의존하지 말고 startTime/endTime 존재 여부 기준
  const hasTimeRange = Boolean(item.startTime && item.endTime);

  // V66-FIX4: 대체휴무 — unit 기준 우선 분류 (전일/오전반차/오후반차/시간단위)
  //   - LeaveUnit('morningHalf'/'afternoonHalf') 와 LeaveTypeCode('halfDayMorning'/'halfDayAfternoon')
  //     양쪽 표기 모두 호환 (실데이터는 'morningHalf'/'afternoonHalf' 단위 사용 중)
  if (item.leaveTypeCode === 'compensatory') {
    if (item.unit === 'morningHalf' || item.unit === 'halfDayMorning') {
      return `${item.leaveTypeName} 오전반차`;
    }
    if (item.unit === 'afternoonHalf' || item.unit === 'halfDayAfternoon') {
      return `${item.leaveTypeName} 오후반차`;
    }
    if (item.unit === 'hourly' || hasTimeRange) {
      const span = formatCompOffDaysHours(item.totalDays); // "1시간" / "3시간" / "1일 2시간"
      if (hasTimeRange) {
        return `${item.leaveTypeName} ${span} (${item.startTime}~${item.endTime})`;
      }
      return `${item.leaveTypeName} ${span}`;
    }
    // fullDay or unit 미상이지만 totalDays >= 1
    return `${item.leaveTypeName} 전일`;
  }

  // 일반 hourly (annual + 시간단위 등)
  if (hasTimeRange && item.unit === 'hourly') {
    return `${item.leaveTypeName} (${item.startTime}~${item.endTime})`;
  }

  return item.leaveTypeName;
}

function entryDisplay(item: OrgLeaveCalendarItem): string {
  // V66: 타팀지원 인원은 이름 뒤에 "(타팀지원)" 표기 — 캘린더 셀 + tooltip 양쪽 동일
  const nameWithFlag = item.isExternalSupport
    ? `${item.employeeName} (타팀지원)`
    : item.employeeName;
  return `${nameWithFlag} ${entryLabel(item)}`;
}

interface CalendarCell {
  date: Date;
  ymdStr: string;
  isCurrentMonth: boolean;
  isWeekend: boolean;
  isHoliday: boolean;
  holidayName?: string;
  entries: OrgLeaveCalendarItem[];
}

// V69 (2026-06-02): KR 격주 재택 금요일 — 공용 helper 사용 ([shared/utils/krRemoteFriday.ts](ngle-works/src/shared/utils/krRemoteFriday.ts))

/**
 * V66-FIX2: 다일 휴가 expand 시 주말/공휴일 제외
 *   - 정책: 휴가가 주말/공휴일을 포함해도 그 날은 표시하지 않음
 *     (실제 근무일 기준 휴가만 표시)
 *   - 단, 시간단위(hourly) / 반차 휴가는 startDate==endDate 인 단일 날이라 영향 없음
 */
function buildCells(
  year: number,
  month1: number,
  items: OrgLeaveCalendarItem[],
  holidaySet: Set<string>
): CalendarCell[] {
  const firstOfMonth = new Date(year, month1 - 1, 1);
  const startWeekday = firstOfMonth.getDay(); // 0=일
  const calStart = new Date(year, month1 - 1, 1 - startWeekday);

  // expand: 각 item을 startDate~endDate 모든 날짜에 매핑 (주말/공휴일 제외)
  // V66: 휴직(LoA)은 인사상태라 기간 전체(주말/공휴일 포함) 표시
  const byDate = new Map<string, OrgLeaveCalendarItem[]>();
  for (const it of items) {
    if (!it.startDate) continue;
    const isLoa = it.kind === 'leaveOfAbsence' || it.leaveTypeCode === 'leaveOfAbsence';
    const cur = new Date(it.startDate + 'T00:00:00');
    const end = new Date((it.endDate || it.startDate) + 'T00:00:00');
    while (cur <= end) {
      const key = ymd(cur);
      const dow = cur.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const isHoliday = holidaySet.has(key);
      // 주말/공휴일이면 expand 제외 (근무일에만 표시) — 단 휴직은 항상 포함
      if (isLoa || (!isWeekend && !isHoliday)) {
        if (!byDate.has(key)) byDate.set(key, []);
        byDate.get(key)!.push(it);
      }
      cur.setDate(cur.getDate() + 1);
    }
  }

  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(calStart);
    d.setDate(calStart.getDate() + i);
    const key = ymd(d);
    const dow = d.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const isHoliday = holidaySet.has(key);
    cells.push({
      date: d,
      ymdStr: key,
      isCurrentMonth: d.getMonth() === month1 - 1,
      isWeekend,
      isHoliday,
      entries: byDate.get(key) || [],
    });
  }
  return cells;
}

export function OrgLeaveCalendarPage(): React.ReactElement {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  // V68: 휴직 인원 표시 토글. 기본 OFF. 월 이동과 독립 — year/month 변경에도 상태 보존.
  const [showLeaveOfAbsence, setShowLeaveOfAbsence] = useState(false);

  const { items, scopeMode, isLoading, error, refetch } = useOrgLeaveCalendar(year, month);

  // V68: 토글 OFF 면 LoA 항목 제외. 휴가 이벤트는 항상 유지.
  //   callable 응답에 이미 leaveOfAbsence 가 동봉되어 있어 FE 필터만으로 처리 가능 (BE 변경 0).
  const visibleItems = useMemo(() => {
    if (showLeaveOfAbsence) return items;
    return items.filter(it => it.kind !== 'leaveOfAbsence' && it.leaveTypeCode !== 'leaveOfAbsence');
  }, [items, showLeaveOfAbsence]);

  // V66-FIX2: 회사별 공휴일 (일반 직원 허용 callable)
  const { user } = useCurrentUser();
  const companyCode = (user?.profile?.companyCode as CompanyCode | undefined) || null;
  const { holidays } = useCalendarHolidays(companyCode, year);
  const holidayMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays) m.set(h.date, h.name);
    return m;
  }, [holidays]);
  const holidaySet = useMemo(() => new Set(holidayMap.keys()), [holidayMap]);

  const cells = useMemo(
    () => buildCells(year, month, visibleItems, holidaySet),
    [year, month, visibleItems, holidaySet]
  );

  const goPrev = useCallback(() => {
    setMonth((m) => {
      if (m === 1) {
        setYear((y) => y - 1);
        return 12;
      }
      return m - 1;
    });
  }, []);

  const goNext = useCallback(() => {
    setMonth((m) => {
      if (m === 12) {
        setYear((y) => y + 1);
        return 1;
      }
      return m + 1;
    });
  }, []);

  const goToday = useCallback(() => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  }, []);

  const scopeLabel =
    scopeMode === 'ALL' ? '전사' : scopeMode === 'SUBTREE' ? '본부 + 하위' : scopeMode === 'SELF' ? '내 조직' : '';

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '56px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CalendarIcon size={20} />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>조직 휴가 캘린더</h2>
          {scopeLabel && (
            <span
              style={{
                fontSize: '12px',
                color: 'var(--color-gray-600)',
                background: 'var(--color-gray-100)',
                padding: '2px 8px',
                borderRadius: '10px',
              }}
            >
              범위: {scopeLabel}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={goPrev}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 600,
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            {year}-{pad2(month)}
          </div>
          <button
            onClick={goNext}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={goToday}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            오늘
          </button>
          <button
            onClick={refetch}
            disabled={isLoading}
            title="새로고침"
            style={{
              padding: '6px 10px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              background: 'white',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <RefreshCw size={14} style={{ animation: isLoading ? 'spin 1s linear infinite' : undefined }} />
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '12px',
            background: 'var(--color-red-50)',
            color: 'var(--color-red-700)',
            borderRadius: '6px',
            marginBottom: '12px',
            fontSize: '13px',
          }}
        >
          {error.message || '데이터 조회 실패'}
        </div>
      )}

      {/* V68: 휴직 인원 표시 토글 — 월 선택 영역 아래. 기본 OFF, 월 이동과 독립 state. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={showLeaveOfAbsence}
            onChange={(e) => setShowLeaveOfAbsence(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          휴직 인원 표시
        </label>
        {showLeaveOfAbsence && (
          <span style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>
            (해당 월 기간 overlap)
          </span>
        )}
      </div>

      {/* 요일 헤더 */}
      <div
        style={{
          display: 'grid',
          // V68: minmax(0, 1fr) — grid 자식의 min-content auto override.
          //   기존 '1fr' 은 nowrap chip(특히 LoA) 의 content 폭이 셀 폭을 늘려 row 깨짐.
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          background: 'var(--color-gray-50)',
          borderTop: '1px solid var(--color-gray-200)',
          borderLeft: '1px solid var(--color-gray-200)',
        }}
      >
        {KOREAN_DAY.map((d, i) => (
          <div
            key={d}
            style={{
              padding: '8px',
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '13px',
              borderRight: '1px solid var(--color-gray-200)',
              borderBottom: '1px solid var(--color-gray-200)',
              color: i === 0 ? '#d33' : i === 6 ? '#33d' : 'inherit',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* 셀 그리드 */}
      <div
        style={{
          display: 'grid',
          // V68: minmax(0, 1fr) — 위 헤더와 동일. nowrap content 가 셀 폭을 늘리지 않도록 강제.
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderLeft: '1px solid var(--color-gray-200)',
        }}
      >
        {cells.map((cell, idx) => {
          const isToday = cell.ymdStr === ymd(today);
          const visible = cell.entries.slice(0, MAX_VISIBLE_PER_CELL);
          // V66: 인원수는 employeeId(없으면 employeeName) 기준 unique count.
          //   같은 직원의 오전반차+오후반차는 1명으로 카운트. 휴가 건수(entries.length)와 분리.
          const getEmpKey = (it: OrgLeaveCalendarItem) => it.employeeId || it.employeeName;
          const uniqueEmployeeCount = new Set(cell.entries.map(getEmpKey)).size;
          const visibleEmpKeys = new Set(visible.map(getEmpKey));
          // overflow 도 사람 수 기준: 셀에 표시 안 된 직원 수
          const overflow = uniqueEmployeeCount - visibleEmpKeys.size;
          const showPopover = hoverDate === cell.ymdStr && cell.entries.length > 0;

          // V66-FIX2: 주말/공휴일 배경 (연한 붉은색)
          const isOff = cell.isWeekend || cell.isHoliday;
          // V69 (2026-06-02): KR 격주 재택 금요일 — 현재월 + 비휴일 + 비주말일 때만 연한 노랑 배경.
          //   - 우선순위: 비현재월 (회색) > 휴일/주말 (연한 붉은색) > KR 재택 금요일 (연한 노랑) > 평일 (흰색)
          //   - 휴일과 겹치면 휴일 빨강 우선 (사용자가 출근 안 함 — 재택 표시 무의미)
          const isKrRemote = companyCode === 'KR' && isKrRemoteFriday(cell.date);
          let bg: string;
          if (!cell.isCurrentMonth) bg = 'var(--color-gray-50)';
          else if (isOff) bg = '#fdecec'; // 연한 붉은색
          else if (isKrRemote) bg = '#FFFBEB'; // very light yellow (재택 금요일)
          else bg = 'white';

          return (
            <div
              key={idx}
              onMouseEnter={() => setHoverDate(cell.ymdStr)}
              onMouseLeave={() => setHoverDate((d) => (d === cell.ymdStr ? null : d))}
              style={{
                position: 'relative',
                minHeight: '110px',
                padding: '6px',
                borderRight: '1px solid var(--color-gray-200)',
                borderBottom: '1px solid var(--color-gray-200)',
                background: bg,
                opacity: cell.isCurrentMonth ? 1 : 0.55,
                cursor: cell.entries.length > 0 ? 'pointer' : 'default',
                // V68: grid 자식 안전망 — chip nowrap content 가 셀 폭을 늘리지 않도록 minWidth:0.
                //   주의: 셀에는 'overflow: hidden' 부여 금지 — 자식 popover(position:absolute)가 clip 됨.
                //   ellipsis 는 chip 자체의 overflow:hidden + textOverflow:ellipsis 로 처리 (line 484~486).
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                  marginBottom: '4px',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                  <span
                    style={{
                      fontWeight: isToday ? 700 : 400,
                      color: isToday
                        ? 'white'
                        : cell.isHoliday || cell.date.getDay() === 0
                        ? '#d33'
                        : cell.date.getDay() === 6
                        ? '#33d'
                        : 'inherit',
                      background: isToday ? 'var(--color-primary)' : 'transparent',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {cell.date.getDate()}
                  </span>
                  {/* V69 (2026-06-02): KR 격주 재택 금요일 — 날짜 옆 chip/badge 형태 "[재택근무]".
                      - companyCode==='KR' + isKrRemoteFriday + 비휴일 일 때만 노출 (휴일이면 휴일명 우선)
                      - 셀 배경(#FFFBEB)에서 살짝 톤 다운된 #FEF3C7 + amber border 로 식별성 확보
                      - 휴가/잔액/근태/지각 영향 0. 표시 전용. pointerEvents:'none' 으로 hover 회귀 방지 */}
                  {isKrRemote && !cell.isHoliday && (
                    <span
                      style={{
                        fontSize: '9px',
                        color: '#D97706',                  // muted amber
                        fontWeight: 600,
                        background: '#FEF3C7',             // light yellow (셀 배경보다 약간 진함)
                        border: '1px solid #FDE68A',       // pale amber border
                        borderRadius: '999px',             // pill
                        padding: '0px 6px',
                        lineHeight: 1.6,
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        flexShrink: 0,
                        letterSpacing: '0.2px',
                      }}
                      title="격주 재택 금요일"
                    >
                      재택근무
                    </span>
                  )}
                </span>
                {cell.isHoliday && holidayMap.get(cell.ymdStr) && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: '#d33',
                      maxWidth: '70%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={holidayMap.get(cell.ymdStr)}
                  >
                    {holidayMap.get(cell.ymdStr)}
                  </span>
                )}
              </div>
              {/* V68: flex column 자식 ellipsis 보장 — minWidth: 0 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                {visible.map((it, i) => {
                  const isLoa = it.kind === 'leaveOfAbsence' || it.leaveTypeCode === 'leaveOfAbsence';
                  // V66: 휴직(LoA)은 옅은 주황 + border, 타팀지원은 보라, 그 외 파랑
                  const bg = isLoa
                    ? '#fff7ed'
                    : it.isExternalSupport
                      ? 'var(--color-purple-50)'
                      : 'var(--color-blue-50)';
                  const color = isLoa
                    ? '#c2410c'
                    : it.isExternalSupport
                      ? 'var(--color-purple-700)'
                      : 'var(--color-blue-700)';
                  return (
                    <div
                      key={`${cell.ymdStr}-${it.leaveRequestId}-${i}`}
                      title={
                        it.isExternalSupport && it.supportProjectName
                          ? `${entryDisplay(it)}  ·  ${it.supportProjectName}`
                          : entryDisplay(it)
                      }
                      style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        background: bg,
                        color,
                        border: isLoa ? '1px solid #fed7aa' : undefined,
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {entryDisplay(it)}
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: 'var(--color-gray-700)',
                      padding: '2px 6px',
                      background: 'var(--color-gray-100)',
                      borderRadius: '4px',
                    }}
                  >
                    +{overflow}명
                  </div>
                )}
              </div>

              {showPopover && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 10,
                    top: '100%',
                    left: 0,
                    minWidth: '260px',
                    maxWidth: '320px',
                    background: 'white',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    padding: '10px',
                    fontSize: '12px',
                  }}
                  onMouseEnter={() => setHoverDate(cell.ymdStr)}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: '6px',
                      paddingBottom: '4px',
                      borderBottom: '1px solid var(--color-gray-200)',
                    }}
                  >
                    {cell.ymdStr} ({uniqueEmployeeCount}명)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '300px', overflowY: 'auto' }}>
                    {cell.entries.map((it, i) => (
                      <div
                        key={`po-${cell.ymdStr}-${it.leaveRequestId}-${i}`}
                        style={{
                          padding: '4px 6px',
                          borderRadius: '4px',
                          // V66: 타팀지원은 popup 도 동일 톤으로 구분
                          background: it.isExternalSupport ? 'var(--color-purple-50)' : 'var(--color-gray-50)',
                        }}
                      >
                        {/* V66: 이름 뒤 "(타팀지원)" 표기 */}
                        <div style={{ fontWeight: 600 }}>
                          {it.employeeName}
                          {it.isExternalSupport && (
                            <span style={{ marginLeft: 6, fontSize: '11px', fontWeight: 500, color: 'var(--color-purple-700)' }}>
                              (타팀지원)
                            </span>
                          )}
                        </div>
                        <div style={{ color: 'var(--color-gray-600)' }}>
                          {it.orgUnitName} · {entryLabel(it)}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>
                          {it.startDate}
                          {it.endDate && it.endDate !== it.startDate ? ` ~ ${it.endDate}` : ''}
                          {it.totalDays ? ` · ${it.totalDays}일` : ''}
                          {it.isExternalSupport && it.supportProjectName ? ` · ${it.supportProjectName}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default OrgLeaveCalendarPage;
